import { Calendar, Clock, FileText, Eye, Loader2, Languages } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RiskLevelBadge } from '../RiskLevelBadge'
import { ChildAvatar } from '../ChildAvatar'
import { DashboardLayout } from '../DashboardLayout'
import { AssessmentStatus, Assessment, Child, Prediction } from '@/types/dashboard.types'
import { AssessmentAPI, ChildAPI, AuthAPI } from '@/services/api'
import { useEffect, useMemo, useState } from 'react'

interface AssessmentWithDetails extends Assessment {
  child: Child
  prediction?: Prediction
}

/** Labels for list badges (short). */
function getStatusBadgeLabel(status: AssessmentStatus): string {
  switch (status) {
    case AssessmentStatus.IN_PROGRESS:
      return 'In progress'
    case AssessmentStatus.COMPLETED:
      return 'Completed'
    case AssessmentStatus.ANALYZED:
      return 'Analyzed'
    default:
      return String(status)
  }
}

/**
 * Longer labels for the status filter dropdown (explains in-app meaning).
 * Backend sets IN_PROGRESS or COMPLETED; ANALYZED exists in the schema but is not assigned by current APIs.
 */
function getStatusFilterDropdownLabel(status: AssessmentStatus): string {
  switch (status) {
    case AssessmentStatus.IN_PROGRESS:
      return 'In progress (still answering)'
    case AssessmentStatus.COMPLETED:
      return 'Completed (submitted, with ML results)'
    case AssessmentStatus.ANALYZED:
      return 'Analyzed (legacy data only)'
    default:
      return String(status)
  }
}

const STATUS_FILTER_ORDER: AssessmentStatus[] = [
  AssessmentStatus.IN_PROGRESS,
  AssessmentStatus.COMPLETED,
  AssessmentStatus.ANALYZED,
]

export const AssessmentHistory = () => {
  const [user, setUser] = useState<any>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [childFilter, setChildFilter] = useState<string>('all')
  const [showEnglish, setShowEnglish] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [assessments, setAssessments] = useState<AssessmentWithDetails[]>([])
  const [childrenList, setChildrenList] = useState<Child[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [u, childrenData, assessmentsPayload] = await Promise.all([
          AuthAPI.me(),
          ChildAPI.getChildren(),
          AssessmentAPI.getAssessments() // Should Ideally filter on backend but for UI simplicity
        ])

        setUser(u.user)
        setChildrenList(childrenData)

        // Handle potentially updated API payload which separates assessments and stats
        const assessmentsData = assessmentsPayload.assessments || assessmentsPayload;

        // Map predictions inside the assessment to prediction key, map child info correctly
        const enriched: AssessmentWithDetails[] = assessmentsData.map((a: any) => ({
          ...a,
          child: childrenData.find((c: Child) => c.id === a.childId)!,
          prediction: a.prediction
        })).filter((a: any) => a.child) // remove those with undefined child

        setAssessments(enriched)
      } catch (err) {
        console.error("Failed fetching Assessment History", err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: AssessmentStatus): string => {
    switch (status) {
      case AssessmentStatus.IN_PROGRESS:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200'
      case AssessmentStatus.COMPLETED:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-200'
      case AssessmentStatus.ANALYZED:
        return 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200'
      default:
        return 'bg-muted text-foreground'
    }
  }

  const getStatusLabel = (status: AssessmentStatus): string => {
    switch (status) {
      case AssessmentStatus.IN_PROGRESS:
        return 'In progress'
      case AssessmentStatus.COMPLETED:
        return 'Completed (with results)'
      case AssessmentStatus.ANALYZED:
        return 'Analyzed (legacy)'
      default:
        return status as string
    }
  }

  const assessmentsInChildScope = useMemo(() => {
    if (childFilter === 'all') return assessments
    return assessments.filter((a) => a.childId === childFilter)
  }, [assessments, childFilter])

  const statusFilterOptions = useMemo(() => {
    const present = new Set(assessmentsInChildScope.map((a) => a.status))
    const rows: { value: string; label: string }[] = [{ value: 'all', label: 'All statuses' }]

    if (childFilter === 'all') {
      for (const s of STATUS_FILTER_ORDER) {
        if (s === AssessmentStatus.ANALYZED && !present.has(s)) continue
        rows.push({ value: s, label: getStatusFilterDropdownLabel(s) })
      }
      return rows
    }

    for (const s of STATUS_FILTER_ORDER) {
      if (present.has(s)) {
        rows.push({ value: s, label: getStatusFilterDropdownLabel(s) })
      }
    }
    return rows
  }, [assessmentsInChildScope, childFilter])

  useEffect(() => {
    if (statusFilter === 'all') return
    const allowed = new Set(statusFilterOptions.map((o) => o.value))
    if (!allowed.has(statusFilter)) {
      setStatusFilter('all')
    }
  }, [statusFilter, statusFilterOptions])

  const filteredAssessments = assessments.filter((assessment) => {
    const matchesStatus = statusFilter === 'all' || assessment.status === statusFilter
    const matchesChild = childFilter === 'all' || assessment.childId === childFilter
    return matchesStatus && matchesChild
  })

  /**
   * Hide abandoned empty drafts: IN_PROGRESS with zero saved answers that started before the
   * child's latest completed assessment (parent finished a session after this shell was created).
   */
  const visibleAssessments = useMemo(() => {
    const latestCompletedByChild = new Map<string, number>()
    for (const a of filteredAssessments) {
      if (!a.completedAt) continue
      const t = new Date(a.completedAt).getTime()
      const prev = latestCompletedByChild.get(a.childId) ?? 0
      if (t > prev) latestCompletedByChild.set(a.childId, t)
    }

    return filteredAssessments.filter((a) => {
      if (a.status !== AssessmentStatus.IN_PROGRESS) return true
      const responses = a._count?.responses ?? 0
      if (responses > 0) return true
      const started = new Date(a.startedAt || a.createdAt).getTime()
      const latestDone = latestCompletedByChild.get(a.childId) ?? 0
      if (latestDone > 0 && started < latestDone) return false
      return true
    })
  }, [filteredAssessments])

  if (isLoading || !user) {
    return (
      <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between animate-in slide-in-from-left duration-700">
          <div className="space-y-2 min-w-0">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
              Assessment History
            </h1>
            <p className="text-muted-foreground text-lg">
              {showEnglish
                ? 'View and manage assessment sessions (each row is one attempt per child).'
                : 'Tingnan at pamahalaan ang mga assessment session — bawat row ay isang pagkakataon (session) bawat bata.'}
            </p>
            {showEnglish ? (
              <p className="text-sm text-muted-foreground italic">
                Filipino: Tingnan at pamahalaan ang mga assessment session — bawat row ay isang pagkakataon bawat bata.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                English: View and manage assessment sessions (each row is one attempt per child).
              </p>
            )}
          </div>
          <Button
            type="button"
            variant={showEnglish ? 'default' : 'outline'}
            size="sm"
            className="gap-2 shrink-0 self-start md:self-auto"
            onClick={() => setShowEnglish((v) => !v)}
            aria-pressed={showEnglish}
          >
            <Languages className="h-4 w-4 shrink-0" aria-hidden />
            {showEnglish ? 'Filipino' : 'English'}
          </Button>
        </div>

        <Card className="border border-teal-200/80 bg-teal-50/40 dark:border-primary/25 dark:bg-primary/10">
          <CardContent className="py-4 text-sm text-teal-900 dark:text-foreground">
            {showEnglish ? (
              <>
                <p className="font-medium">Why do I see more than one row for the same child?</p>
                <p className="mt-1 text-teal-800/90 dark:text-muted-foreground">
                  Each row is a separate assessment session. After you finish one, you can start another — it is normal
                  to see both a <strong>Completed</strong> row (with report) and an <strong>In progress</strong> row
                  if you opened a new assessment and have not submitted it yet.
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">Bakit may higit sa isang row para sa iisang bata?</p>
                <p className="mt-1 text-teal-800/90 dark:text-muted-foreground">
                  Bawat row ay hiwalay na assessment session. Pagkatapos mag-submit ng isa, puwede kang mag-start ng
                  bago — normal na may <strong>Completed</strong> (may report) at <strong>In progress</strong> kung
                  may bagong assessment kang binuksan at hindi pa natatapos.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="border-transparent bg-gradient-to-br from-teal-50 to-teal-100/50 backdrop-blur-sm dark:from-card dark:to-muted/50 dark:border dark:border-border animate-in slide-in-from-top duration-700">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Select value={childFilter} onValueChange={setChildFilter}>
                  <SelectTrigger aria-label="Filter by child">
                    <SelectValue placeholder="All Children" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Children</SelectItem>
                    {childrenList.filter((c) => c.isActive !== false).map((child) => (
                      <SelectItem key={child.id} value={child.id}>
                        {child.firstName} {child.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger aria-label="Filter by status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusFilterOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assessment List */}
        <div className="space-y-4 animate-in slide-in-from-bottom duration-700">
          {visibleAssessments.length === 0 ? (
            <Card className="border-transparent bg-muted/30 backdrop-blur-sm animate-in fade-in duration-500">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
                <h3 className="text-lg font-semibold mb-2">No Assessments Found</h3>
                <p className="text-muted-foreground text-center">
                  {statusFilter !== 'all' || childFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Start a new assessment to see it here'}
                </p>
              </CardContent>
            </Card>
          ) : (
            visibleAssessments.map((assessment) => (
              <Card key={assessment.id} className="border-transparent bg-card hover:border-border hover:shadow-lg transition-all animate-in slide-in-from-bottom duration-700 dark:border dark:border-border">
                <CardHeader className="pb-4">
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <ChildAvatar gender={assessment.child.gender} size="sm" />
                      <div>
                        <CardTitle className="text-lg">
                          {assessment.child.firstName} {assessment.child.lastName}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getStatusColor(assessment.status)}>
                            {getStatusBadgeLabel(assessment.status)}
                          </Badge>
                          {assessment.prediction && (
                            <RiskLevelBadge riskLevel={assessment.prediction.riskLevel} size="sm" />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {assessment.prediction &&
                        (assessment.status === AssessmentStatus.COMPLETED ||
                          assessment.status === AssessmentStatus.ANALYZED) && (
                        <Link
                          to="/dashboard/reports/$assessmentId"
                          params={{ assessmentId: assessment.id }}
                        >
                          <Button
                            size="default"
                            variant="default"
                            className="gap-2"
                            aria-label={`View ${assessment.child.firstName}'s assessment report`}
                          >
                            <Eye className="h-4 w-4" aria-hidden="true" />
                            View report
                          </Button>
                        </Link>
                      )}
                      {assessment.status === AssessmentStatus.IN_PROGRESS && (
                        <Link
                          to="/dashboard/assessment/$childId"
                          params={{ childId: assessment.childId }}
                          search={{ resume: 'true' }}
                        >
                          <Button
                            size="default"
                            className="gap-2 bg-blue-600 hover:bg-blue-700"
                            aria-label={`Continue ${assessment.child.firstName}'s assessment`}
                          >
                            Continue assessment
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" aria-hidden="true" />
                      <div>
                        <div className="font-medium text-foreground">Started</div>
                        <div>{formatDate(assessment.createdAt)}</div>
                      </div>
                    </div>
                    {assessment.completedAt && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" aria-hidden="true" />
                        <div>
                          <div className="font-medium text-foreground">Completed</div>
                          <div>{formatDateTime(assessment.completedAt)}</div>
                        </div>
                      </div>
                    )}
                    {assessment.prediction && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FileText className="h-4 w-4" aria-hidden="true" />
                        <div>
                          <div className="font-medium text-foreground">Confidence</div>
                          <div>{(assessment.prediction.confidenceScore * 100).toFixed(1)}%</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {assessment.prediction && Array.isArray(assessment.prediction.domainScores) && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-2">Domain Scores</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {assessment.prediction.domainScores.map((domainScore: any) => (
                          <div key={domainScore.domain} className="text-center">
                            <div className="text-2xl font-bold text-primary">
                              {domainScore.percentage.toFixed(0)}%
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {domainScore.domain.replace(/_/g, ' ').toLowerCase()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
